<script setup lang="ts">
import type { NuxtError } from "#app";
import type { RouteObject } from "@shopware/composables";
import { getCategoryRoute, getTranslatedProperty } from "@shopware/helpers";
import type { Schemas } from "#shopware";
// Navigation for default theme (depth 1 is 2 level navi, depth 2 is 3 level navi, and so on)
const { loadNavigationElements } = useNavigation();
const { data } = useAsyncData("mainNavigation", () => {
  return loadNavigationElements({ depth: 1 });
});
provide("swNavigation-main-navigation", data);

const { loadNavigationElements: loadFooterNavigationElements } = useNavigation({
  type: "footer-navigation",
});
const { data: footerData } = useAsyncData("mainFooterNavigation", () => {
  return loadFooterNavigationElements({ depth: 1 });
});
provide("swNavigation-footer-navigation", footerData);

const { loadNavigationElements: loadTopNavigationElements } = useNavigation({
  type: "service-navigation",
});
const { data: topNavigationData } = useAsyncData( "mainServiceNavigation", ()=> {
  return loadTopNavigationElements({depth: 1})
})
provide("swNavigation-service-navigation", topNavigationData)

defineOptions({
  name: "ErrorPage",
});

const props = defineProps<{
  error: NuxtError<{
    statusCode: number;
    statusMessage: string;
    message: string;
  }>;
}>();

const isMaintenanceMode = computed(() => {
  return props.error.statusMessage === "MAINTENANCE_MODE";
});

const { t } = useI18n();
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);

let isFormattedError = true;
let errMessage = "";
let linkFormatter = (path: string | RouteObject) => path;

try {
  linkFormatter = formatLink;

  const errorMessageMap: { [key: number]: string } = {
    404: t("errorPages.404"),
    408: t("errorPages.408"),
    500: t("errorPages.500"),
    502: t("errorPages.502"),
    503: t("errorPages.503"),
  };

  errMessage =
    errorMessageMap[props.error.statusCode as keyof typeof errorMessageMap] ||
    props.error.message;

  if (props.error.statusCode !== 404) {
    console.error("[error.vue]:", props.error?.message);
    console.error("[error.vue]:", props.error);
  }

  if (props.error.statusCode === 412) {
    // setting a timeout here to ensure we are the last error message in terminal
    setTimeout(() => {
      console.error(
        "Looks like your API connection is not working. Check your _nuxt.config.ts_ configuration (shopware.endpoint and shopware.accessToken). 🤞",
      );
      console.error(
        "For more help ➡️  https://frontends.shopware.com/resources/troubleshooting.html",
      );
    }, 2.0 * 1000);
  }

} catch (e) {
  console.error("Problem with loading error page", e);
  isFormattedError = false;
}

const statusCode = isFormattedError ? props.error.statusCode : "Error";
const errorMessage = isFormattedError ? errMessage : props.error.message;

// Helper to recursively find a category by ID in the navigation tree
type NavigationElement = Schemas["Category"];
function findCategoryById(
  navigation: NavigationElement[] | undefined,
  id: string
): NavigationElement | undefined {
  if (!navigation) return undefined;
  for (const nav of navigation) {
    if (nav.id === id) return nav;
    if (nav.children && nav.children.length > 0) {
      const found = findCategoryById(nav.children, id);
      if (found) return found;
    }
  }
  return undefined;
}

// Get the main navigation tree (from useAsyncData)
const mainNavigationTree = computed(() => {
  // data.value is expected to be an array of navigation elements
  return data.value as NavigationElement[] | undefined;
});

// The category ID to look for
const categoryId = "01912be3486a7262b5be2f4d7e2d6ed4";

// Find the category in the navigation tree
const navigationCategory = computed(() =>
  findCategoryById(mainNavigationTree.value, categoryId)
);

// Always return a string path for <a :href>
const categoryHref = computed(() => {
  if (!navigationCategory.value) return '/';
  const route = formatLink(getCategoryRoute(navigationCategory.value));
  if (typeof route === 'string') return route;
  if (route && typeof route === 'object' && 'path' in route) return route.path;
  return '/';
});

</script>

<template>
  <div>
  <LayoutHeader />
  <ErrorsMaintainMode v-if="isMaintenanceMode" />
  <div
    v-else
    class="px-5 py-32 md:py-12 md:px-0 lg:px-24 lg:py-14 items-center relative flex justify-center flex-wrap flex-row md:flex-row"
  >
    <div class="flex flex-col items-center justify-center my-8">
      <div class="max-w-full flex md:block md:max-w-md text-center">
        <h1 class="mb-4 font-[900] text-[8rem] md:text-[13rem] lg:text-[18rem] text-pink  leading-[1]">
          <span class="sr-only ">{{ t("error") }}</span
          >{{ statusCode }}
        </h1>
        <p
          v-if="errorMessage"
          class="text-xl hidden md:block md:text-[25px] lg:text-[40px] uppercase font-bold font-figtree  mt-4 mb-6 text-left leading-8 lg:leading-10 "
          v-html="errorMessage"
        >
        </p>
        <div class="flex items-center justify-center md:-mt-40 md:-ml-16 lg:ml-4 block md:hidden w-[124px] -ml-14 -mt-4">
          <img
            class="w-full h-full max-w-[10rem] md:max-w-md max-h-md mt-[5px] md:mt-0"
            src="~/assets/error-image.png"
            alt="Error"
          />
        </div>
        <div class="flex items-center justify-start hidden md:block">
          <NuxtLink
            v-if="navigationCategory"
            :target="
              navigationCategory.externalLink || navigationCategory.linkNewTab
                ? '_blank'
                : ''
            "
            :to="navigationCategory ? formatLink(getCategoryRoute(navigationCategory)) : '/'"
            class="btn-outline min-w-[max-content] py-2 px-10 w-[60%]"
          >
          {{ t("errorPages.backToCategory") }}
          </NuxtLink>
        </div>
      </div>
      
    </div>
    <div class="flex items-center justify-center md:-mt-0 lg:-mt-40 md:-ml-20 lg:ml-4 hidden md:block">
      <img
        class="w-full h-full max-w-[10rem] md:max-w-md max-h-md mt-[5px] md:mt-0"
        src="~/assets/error-image.png"
        alt="Error"
      />
    </div>
    <div class="w-full flex-1 block md:hidden"
    >
      <p
          v-if="errorMessage"
          class="text-xl md:text-[25px] lg:text-[40px] uppercase font-bold font-figtree  mt-4 mb-6 text-center leading-6 lg:leading-10 "
          v-html="errorMessage"
        >
        </p>
        <div class="flex items-center justify-center  md:hidden">
          <NuxtLink
            v-if="navigationCategory"
            :target="
              navigationCategory.externalLink || navigationCategory.linkNewTab
                ? '_blank'
                : ''
            "
            :to="navigationCategory ? formatLink(getCategoryRoute(navigationCategory)) : '/'"
            class="btn-outline min-w-[max-content] py-2 px-10 w-[60%]"
          >
          {{ t("errorPages.backToCategory") }}
          </NuxtLink>
        </div>
    </div>
  </div>
  <LayoutFooter />
  </div>
</template>
